import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Check, X, Pencil, GripVertical } from "lucide-react";
import {
  fetchCategories, createCategory, updateCategory, deleteCategory, reorderCategories, formatApiError,
} from "../../lib/api";

export default function CategoriesEditor() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const dragRef = useRef(null);

  const refresh = useCallback(async () => {
    const c = await fetchCategories();
    setCats(c);
    return c;
  }, []);

  useEffect(() => { refresh().finally(() => setLoading(false)); }, [refresh]);

  const add = async (e) => {
    e.preventDefault();
    if (!newName.trim() || busy) return;
    setBusy(true);
    try {
      await createCategory(newName.trim());
      setNewName("");
      await refresh();
      toast.success("Category added");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const saveRename = async (id) => {
    if (!editName.trim()) return;
    try {
      await updateCategory(id, { name: editName.trim() });
      setEditingId(null);
      await refresh();
      toast.success("Category renamed");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove "${name}"? It will be removed from all projects.`)) return;
    try {
      await deleteCategory(id);
      await refresh();
      toast.success("Category removed");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onDrop = (targetIdx) => {
    const from = dragRef.current;
    dragRef.current = null;
    setDragIdx(null);
    if (from === null || from === targetIdx) return;
    const arr = [...cats];
    const [moved] = arr.splice(from, 1);
    arr.splice(targetIdx, 0, moved);
    setCats(arr);
    reorderCategories(arr.map((c) => c.id)).catch((err) => { toast.error(formatApiError(err)); refresh(); });
  };

  return (
    <div data-testid="categories-editor" className="max-w-xl space-y-8">
      <div>
        <h3 className="font-display text-3xl mb-1">Categories</h3>
        <p className="font-mono text-[11px] text-[var(--vsl-muted)]">Add, rename, remove and drag to reorder the categories used across projects.</p>
      </div>

      <form onSubmit={add} className="flex gap-2" data-testid="create-category-form">
        <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New category name"
          data-testid="new-category-name"
          className="flex-1 bg-transparent border border-[var(--vsl-border)] px-3 py-2.5 font-mono text-sm outline-none focus:border-[var(--vsl-fg)]" />
        <button type="submit" data-testid="add-category-btn" disabled={busy}
          className="bg-[var(--vsl-fg)] text-[var(--vsl-bg)] px-4 font-mono text-xs uppercase tracking-wider inline-flex items-center gap-1 hover:bg-[var(--vsl-limestone)] transition-colors disabled:opacity-50">
          <Plus size={14} /> Add
        </button>
      </form>

      {loading ? (
        <p className="font-mono text-xs text-[var(--vsl-muted)]">Loading…</p>
      ) : cats.length === 0 ? (
        <p className="font-mono text-xs text-[var(--vsl-muted)]">No categories yet. Add one above.</p>
      ) : (
        <ul className="space-y-2" data-testid="category-list">
          {cats.map((c, i) => (
            <li key={c.id} data-testid={`category-row-${c.id}`}
              draggable={editingId !== c.id}
              onDragStart={() => { dragRef.current = i; setDragIdx(i); }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(i)}
              onDragEnd={() => { dragRef.current = null; setDragIdx(null); }}
              className={`flex items-center gap-3 border border-[var(--vsl-border)] p-2.5 bg-[var(--vsl-bg)] transition-opacity ${dragIdx === i ? "opacity-40" : ""} ${editingId === c.id ? "" : "cursor-move"}`}>
              <GripVertical size={15} className="text-[var(--vsl-muted)] shrink-0" data-testid={`cat-grip-${c.id}`} />
              {editingId === c.id ? (
                <>
                  <input value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus
                    data-testid={`cat-edit-input-${c.id}`}
                    onKeyDown={(e) => e.key === "Enter" && saveRename(c.id)}
                    className="flex-1 bg-transparent border border-[var(--vsl-border)] px-2 py-1.5 font-mono text-sm outline-none focus:border-[var(--vsl-fg)]" />
                  <button onClick={() => saveRename(c.id)} data-testid={`cat-save-${c.id}`}
                    className="text-[var(--vsl-limestone)] hover:opacity-70"><Check size={16} /></button>
                  <button onClick={() => setEditingId(null)} data-testid={`cat-cancel-${c.id}`}
                    className="text-[var(--vsl-muted)] hover:text-[var(--vsl-fg)]"><X size={16} /></button>
                </>
              ) : (
                <>
                  <span className="flex-1 font-mono text-sm">{c.name}</span>
                  <button onClick={() => { setEditingId(c.id); setEditName(c.name); }} data-testid={`cat-rename-${c.id}`}
                    className="text-[var(--vsl-muted)] hover:text-[var(--vsl-fg)] transition-colors"><Pencil size={14} /></button>
                  <button onClick={() => remove(c.id, c.name)} data-testid={`cat-delete-${c.id}`}
                    className="text-[var(--vsl-muted)] hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
